import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Loader2,
  RotateCcw,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import toast from 'react-hot-toast';
import documentService from '../../services/document.service';
import VirtualizedPdf from '../../components/pdf/VirtualizedPdf';

const pdfWorkerPublicPath = `${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/pdf.worker.min.js`;
if (typeof window !== 'undefined' && pdfjs.GlobalWorkerOptions.workerSrc !== pdfWorkerPublicPath) {
  pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerPublicPath;
}

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;
const WHEEL_ZOOM_STEP = 0.1;

function readViewerPayloadFromWindowName(): {
  documentId?: string;
  documentName?: string;
} {
  try {
    if (!window.name) return {};
    const parsed = JSON.parse(window.name) as {
      documentId?: string;
      documentName?: string;
    };
    return parsed || {};
  } catch {
    return {};
  }
}

const DocumentViewerPage: React.FC = () => {
  const params = useParams<{ documentId?: string }>();
  const [searchParams] = useSearchParams();
  const payload = readViewerPayloadFromWindowName();
  const documentId = payload.documentId || params.documentId || '';
  const requestedName = payload.documentName || searchParams.get('name') || '';
  const docName = requestedName.trim() || `Document-${documentId || ''}`;

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [zoom, setZoom] = useState(1);
  const [pdfNumPages, setPdfNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState('1');
  const [privacyMask, setPrivacyMask] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const blobUrlRef = useRef<string | null>(null);

  const isPdf = useMemo(
    () => (mimeType || '').toLowerCase().includes('pdf'),
    [mimeType]
  );
  const isImage = useMemo(
    () => (mimeType || '').toLowerCase().startsWith('image/'),
    [mimeType]
  );
  const totalPageDigits = Math.max(1, String(pdfNumPages || 1).length);
  const pageBoxWidthPx = Math.max(76, totalPageDigits * 14 + 28);
  const totalBoxWidthPx = Math.max(54, totalPageDigits * 12 + 22);

  const setZoomClamped = (next: number) => {
    const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
    setZoom(Math.round(clamped * 100) / 100);
  };

  useEffect(() => {
    document.title = docName;
  }, [docName]);

  useEffect(() => {
    const blockContextMenu = (e: MouseEvent) => e.preventDefault();
    const blockDrag = (e: DragEvent) => e.preventDefault();
    const blockKeys = (e: KeyboardEvent) => {
      const key = String(e.key || '').toLowerCase();
      const ctrlOrMeta = e.ctrlKey || e.metaKey;
      if (
        (ctrlOrMeta && (key === 's' || key === 'p' || key === 'u')) ||
        key === 'printscreen' ||
        (ctrlOrMeta && e.shiftKey && key === 's') ||
        (e.metaKey && e.shiftKey && (key === '3' || key === '4' || key === '5')) ||
        key === 'f12' ||
        (ctrlOrMeta && e.shiftKey && key === 'i')
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (
        key === 'printscreen' ||
        (ctrlOrMeta && e.shiftKey && key === 's') ||
        (e.metaKey && e.shiftKey && (key === '3' || key === '4' || key === '5'))
      ) {
        setPrivacyMask(true);
        window.setTimeout(() => setPrivacyMask(false), 1200);
      }
      if (ctrlOrMeta && (key === '+' || key === '=')) {
        e.preventDefault();
        e.stopPropagation();
        setZoomClamped(zoom + ZOOM_STEP);
      }
      if (ctrlOrMeta && key === '-') {
        e.preventDefault();
        e.stopPropagation();
        setZoomClamped(zoom - ZOOM_STEP);
      }
      if (ctrlOrMeta && key === '0') {
        e.preventDefault();
        e.stopPropagation();
        setZoomClamped(1);
      }
    };
    const blockBrowserWheelZoom = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const direction = e.deltaY < 0 ? 1 : -1;
      setZoomClamped(zoom + direction * WHEEL_ZOOM_STEP);
    };
    window.addEventListener('contextmenu', blockContextMenu);
    window.addEventListener('dragstart', blockDrag);
    window.addEventListener('keydown', blockKeys, true);
    window.addEventListener('keyup', blockKeys, true);
    window.addEventListener('wheel', blockBrowserWheelZoom, {
      passive: false,
      capture: true,
    });
    return () => {
      window.removeEventListener('contextmenu', blockContextMenu);
      window.removeEventListener('dragstart', blockDrag);
      window.removeEventListener('keydown', blockKeys, true);
      window.removeEventListener('keyup', blockKeys, true);
      window.removeEventListener('wheel', blockBrowserWheelZoom, true);
    };
  }, [zoom]);

  useEffect(() => {
    let unmaskTimer: number | null = null;
    const mask = () => setPrivacyMask(true);
    const unmask = () => {
      if (unmaskTimer) window.clearTimeout(unmaskTimer);
      unmaskTimer = window.setTimeout(() => setPrivacyMask(false), 250);
    };
    const handleVisibility = () => {
      if (document.hidden) mask();
      else unmask();
    };
    window.addEventListener('blur', mask);
    window.addEventListener('focus', unmask);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      if (unmaskTimer) window.clearTimeout(unmaskTimer);
      window.removeEventListener('blur', mask);
      window.removeEventListener('focus', unmask);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  useEffect(() => {
    if (!documentId) {
      setError('Invalid document');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    setBlobUrl(null);
    setMimeType('');
    setPdfNumPages(0);
    setCurrentPage(1);
    setPageInputValue('1');
    pageRefs.current = {};

    documentService
      .getFileForPreview(String(documentId))
      .then(({ url, mimeType: mt }) => {
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = url;
        setBlobUrl(url);
        setMimeType(mt || '');
        setLoading(false);
      })
      .catch((err: any) => {
        setError(err?.message || 'Failed to load document');
        setLoading(false);
      });

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [documentId]);

  useEffect(() => {
    if (pdfNumPages <= 0) return;
    setCurrentPage((prev) => Math.min(Math.max(prev, 1), pdfNumPages));
  }, [pdfNumPages]);

  useEffect(() => {
    setPageInputValue(String(currentPage));
  }, [currentPage]);

  const goToPage = (requested: number) => {
    if (!isPdf || pdfNumPages <= 0) return;
    const next = Math.min(Math.max(requested, 1), pdfNumPages);
    setCurrentPage(next);
    setPageInputValue(String(next));
    const target = pageRefs.current[next];
    const container = scrollRef.current;
    if (target && container) {
      container.scrollTo({
        top: Math.max(0, target.offsetTop - 8),
        left: 0,
        behavior: 'smooth',
      });
    }
  };

  // Current page is derived from `VirtualizedPdf` via IntersectionObserver,
  // which avoids O(n) scans on every scroll for large PDFs.

  const commitPageInput = () => {
    const parsed = Number.parseInt(pageInputValue, 10);
    if (Number.isNaN(parsed)) {
      setPageInputValue(String(currentPage));
      return;
    }
    goToPage(parsed);
  };

  const handleDownload = async () => {
    if (!documentId || downloading) return;
    setDownloading(true);
    try {
      await documentService.downloadDocument(String(documentId), docName);
    } catch (err: any) {
      toast.error(err?.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleWheelZoom = (e: React.WheelEvent<HTMLDivElement>) => {
    // Match browser PDF behavior: Ctrl/Cmd + wheel zooms document, not whole page.
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    e.stopPropagation();
    const direction = e.deltaY < 0 ? 1 : -1;
    setZoomClamped(zoom + direction * WHEEL_ZOOM_STEP);
  };

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-[#2f343a]">
      {privacyMask && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black text-sm font-medium text-white">
          Protected preview
        </div>
      )}
      <div className="sticky top-0 z-30 flex h-12 items-center border-b border-[#1f2328] bg-[#32373d] px-3 text-[#e5e7eb]">
        <button
          type="button"
          className="mr-2 rounded p-1 text-[#cdd3da] hover:bg-[#3d434b]"
          title="Document"
        >
          <FileText size={14} />
        </button>
        <span className="max-w-[28rem] truncate text-xs font-medium">{docName}</span>
        <div className="ml-auto flex items-center gap-2">
          {isPdf && pdfNumPages > 0 && (
            <>
              <button
                type="button"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage <= 1}
                className="rounded p-1 text-[#d1d5db] hover:bg-[#3d434b] disabled:opacity-40"
              >
                <ChevronLeft size={14} />
              </button>
              <input
                type="text"
                inputMode="numeric"
                value={pageInputValue}
                onChange={(e) =>
                  setPageInputValue(e.target.value.replace(/[^\d]/g, ''))
                }
                onBlur={commitPageInput}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitPageInput();
                }}
                className="rounded border border-[#4b5563] bg-[#1f2328] px-1 py-0.5 text-center text-xs tabular-nums text-[#e5e7eb]"
                style={{
                  width: `${pageBoxWidthPx}px`,
                  minWidth: `${pageBoxWidthPx}px`,
                  paddingLeft: '6px',
                  paddingRight: '6px',
                  lineHeight: 1.2,
                  letterSpacing: 0,
                  fontVariantNumeric: 'tabular-nums',
                }}
              />
              <span
                className="rounded bg-[#1f2328] px-1 text-xs tabular-nums text-[#d1d5db]"
                style={{ minWidth: `${totalBoxWidthPx}px` }}
              >
                / {pdfNumPages}
              </span>
              <button
                type="button"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage >= pdfNumPages}
                className="rounded p-1 text-[#d1d5db] hover:bg-[#3d434b] disabled:opacity-40"
              >
                <ChevronRight size={14} />
              </button>
              <span className="mx-1 h-4 w-px bg-[#4b5563]" />
            </>
          )}
          <button
            type="button"
            onClick={() =>
              setZoomClamped(zoom - ZOOM_STEP)
            }
            className="rounded p-1 text-[#d1d5db] hover:bg-[#3d434b]"
          >
            <ZoomOut size={14} />
          </button>
          <span className="min-w-[3rem] text-center text-xs">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            onClick={() =>
              setZoomClamped(zoom + ZOOM_STEP)
            }
            className="rounded p-1 text-[#d1d5db] hover:bg-[#3d434b]"
          >
            <ZoomIn size={14} />
          </button>
          <button
            type="button"
            onClick={() => {
              setZoom(1);
              scrollRef.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-[#d1d5db] hover:bg-[#3d434b]"
          >
            <RotateCcw size={12} />
            Reset
          </button>
          <span className="mx-1 h-4 w-px bg-[#4b5563]" />
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading || loading || !!error}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-[#d1d5db] hover:bg-[#3d434b] disabled:cursor-not-allowed disabled:opacity-40"
            title="Download"
          >
            {downloading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Download size={12} />
            )}
            Download
          </button>
        </div>
      </div>

      <div className="flex h-[calc(100vh-48px)]" onWheel={handleWheelZoom}>
        {isPdf && blobUrl && !loading && !error && (
          <aside className="sticky top-12 h-[calc(100vh-48px)] w-[122px] overflow-y-auto border-r border-[#1f2328] bg-[#20242a] p-2">
            <Document file={blobUrl} loading={null}>
              {pdfNumPages > 0 &&
                (() => {
                  const windowSize = 18; // thumbnails are expensive; keep small window
                  const from = Math.max(1, currentPage - windowSize);
                  const to = Math.min(pdfNumPages, currentPage + windowSize);
                  const items: React.ReactNode[] = [];
                  if (from > 1) {
                    items.push(
                      <button
                        key="thumb-first"
                        type="button"
                        onClick={() => goToPage(1)}
                        className="mb-2 w-full rounded border border-[#374151] bg-[#262b33] p-1 text-[10px] text-[#cbd5e1] hover:bg-[#2e3440]"
                      >
                        Jump to 1
                      </button>
                    );
                    items.push(
                      <div
                        key="thumb-gap-top"
                        className="mb-2 text-center text-[10px] text-[#94a3b8]"
                      >
                        …
                      </div>
                    );
                  }
                  for (let pageNo = from; pageNo <= to; pageNo += 1) {
                    const active = pageNo === currentPage;
                    items.push(
                      <button
                        key={`thumb-${pageNo}`}
                        type="button"
                        onClick={() => goToPage(pageNo)}
                        className={`mb-2 w-full rounded border p-1 ${
                          active
                            ? 'border-blue-500 bg-[#2e3440]'
                            : 'border-[#374151] bg-[#262b33]'
                        }`}
                      >
                        <Page
                          pageNumber={pageNo}
                          width={88}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          canvasBackground="white"
                        />
                        <div className="mt-1 text-center text-[10px] text-[#cbd5e1]">
                          {pageNo}
                        </div>
                      </button>
                    );
                  }
                  if (to < pdfNumPages) {
                    items.push(
                      <div
                        key="thumb-gap-bottom"
                        className="mb-2 text-center text-[10px] text-[#94a3b8]"
                      >
                        …
                      </div>
                    );
                    items.push(
                      <button
                        key="thumb-last"
                        type="button"
                        onClick={() => goToPage(pdfNumPages)}
                        className="mb-2 w-full rounded border border-[#374151] bg-[#262b33] p-1 text-[10px] text-[#cbd5e1] hover:bg-[#2e3440]"
                      >
                        Jump to {pdfNumPages}
                      </button>
                    );
                  }
                  return items;
                })()}
            </Document>
          </aside>
        )}

        <main
          ref={scrollRef}
          className="h-full flex-1 overflow-auto bg-[#3a3f45]"
          data-pdf-scroll-root={isPdf ? 'true' : undefined}
        >
          {loading && (
            <div className="p-6 text-center text-sm text-[#e5e7eb]">Loading document...</div>
          )}
          {!loading && error && (
            <div className="p-6 text-center text-sm text-[#fecaca]">{error}</div>
          )}

          {!loading && !error && blobUrl && isImage && (
            <div className="mx-auto w-fit p-4">
              <img
                src={blobUrl}
                alt={docName}
                className="block bg-white shadow"
                style={{ width: `${100 * zoom}%`, height: 'auto' }}
                draggable={false}
              />
            </div>
          )}

          {!loading && !error && blobUrl && isPdf && (
            <div className="flex min-h-full flex-col items-center gap-4 p-4">
              <VirtualizedPdf
                file={blobUrl}
                scale={zoom}
                pageClassName="shadow"
                currentPage={currentPage}
                onLoadSuccess={({ numPages }) => {
                  setPdfNumPages(numPages);
                  setCurrentPage(1);
                }}
                onLoadError={() => setError('Failed to render PDF')}
                onPageWrapperRef={(pageNumber, el) => {
                  pageRefs.current[pageNumber] = el;
                }}
                onCurrentPageChange={(page) => {
                  if (page !== currentPage) setCurrentPage(page);
                }}
                config={{ rootMarginPx: 1800, estimatedPageHeightPx: 1100 }}
              />
            </div>
          )}

          {!loading && !error && blobUrl && !isPdf && !isImage && (
            <div className="p-6 text-center text-sm text-[#e5e7eb]">
              This file type cannot be previewed in protected tab mode.
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default DocumentViewerPage;
